// ────────────────────────────────────────────────────────────────────
// The filter row over the document list.
//
// Three visible chips, one default, one disclosure. Deliberately NOT a filter
// by file extension: _guess_kind already collapses PDF and Word onto `report`,
// and nobody looks for "a .docx" — they look for the UT report for TK-101 from
// 2024. The extension belongs on the row as an affordance, not here as a facet.
//
// Current-revision-only is a DEFAULT rather than a chip because it is what is
// wanted almost every time; the escape hatch is one click and the list says so
// when superseded revisions are included.
// ────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";

export interface DocumentFacets {
  years: number[];
  undated: number;
  kinds: Record<string, number>;
}

/** How many documents a year filter is hiding, counted over the rows that are
 *  actually in scope — the open folder, narrowed by every OTHER active filter,
 *  before the year one is applied.
 *
 *  Deliberately NOT `DocumentFacets`: facets.undated is a tenant-wide count,
 *  unscoped by folder, kind or direction, and putting it in a folder-scoped
 *  sentence tells the reader "412 more are undated" while standing in a folder
 *  of three. The caller computes this from the list it already has (the list is
 *  narrowed client-side), so no backend parameter is involved. */
export interface UndatedInScope {
  undated: number;
}

export interface FilterValue {
  kind?: string;
  year?: string;          // "2024" | "none"
  direction?: string;
  currentOnly: boolean;
}

const KIND_LABEL: Record<string, string> = {
  report: "รายงาน", data: "ข้อมูลดิบ", drawing: "แบบ", photo: "รูปถ่าย", other: "อื่นๆ",
};
const DIRECTION_LABEL: Record<string, string> = {
  in: "เข้า", out: "ออก", internal: "ภายใน",
};

/** Same "Thai label (raw key)" shape for any facet whose key is an English
 *  enum value — one treatment for the idea "narrowed by X", not a different
 *  one per facet. Used for both kind and direction below. */
function withKey(label: string | undefined, key: string): string {
  return label ? `${label} (${key})` : key;
}

/** Why the list is empty, in the reader's terms. A bare "no results" invites the
 *  conclusion that the archive is empty rather than narrowed — and a year filter
 *  additionally hides every undated document by construction, which the reader
 *  has no way to know. The raw `kind`/`direction` key rides alongside its Thai
 *  label (not instead of it) because this string doubles as the thing a support
 *  thread quotes back at us, and "report"/"internal" are what appear in the
 *  API/DB, not "รายงาน"/"ภายใน".
 *
 *  `currentOnly` is a DEFAULT, not something the reader switched on, so it does
 *  not by itself make the list "narrowed": a first-run tenant with an empty
 *  archive must be told the archive is empty, not handed
 *  "ไม่พบเอกสาร — ฉบับปัจจุบัน", which reads as a filter they should go undo.
 *  Once a real filter IS active, the default is named alongside it — at that
 *  point the reader is auditing what emptied the list and it is part of the
 *  answer. */
export function emptyReason(v: FilterValue, scope: UndatedInScope): string {
  const parts: string[] = [];
  if (v.kind) parts.push(withKey(KIND_LABEL[v.kind], v.kind));
  if (v.year === "none") parts.push("ไม่ระบุวันที่");
  else if (v.year) parts.push(`ปี ${v.year}`);
  if (v.direction) parts.push(withKey(DIRECTION_LABEL[v.direction], v.direction));
  if (!parts.length) return "ไม่มีเอกสาร";
  if (v.currentOnly) parts.push("ฉบับปัจจุบัน");
  const head = `ไม่พบเอกสาร — ${parts.join(" · ")}`;
  if (v.year && v.year !== "none" && scope.undated > 0) {
    return `${head} · อีก ${scope.undated} ฉบับยังไม่ระบุวันที่ ตัวกรองปีจึงมองไม่เห็น`;
  }
  return head;
}

const chip = "font-mono uppercase px-2 py-1 border transition";
const chipStyle = (on: boolean) => ({
  fontSize: 9.5, letterSpacing: ".1em",
  color: on ? "var(--accent-deep)" : "var(--ink-3)",
  borderColor: on ? "var(--accent)" : "var(--hairline)",
});

/** A chip that opens a small option list on click. Deliberately NOT a native
 *  <select> — a <select>'s accessible role is "combobox", and this row's own
 *  test coverage (and screen readers navigating by role) treats each facet as
 *  a button that discloses its choices, matching the "more filters" button
 *  next to it. `allLabel` is shown (and is the accessible name) when nothing
 *  is picked; it always contains `title` as a substring so "which chip is
 *  this" is answerable without opening it. */
function FacetDropdown({ title, allLabel, value, options, onSelect }: {
  title: string;
  allLabel: string;
  value?: string;
  options: { value: string; label: string }[];
  onSelect: (v: string | undefined) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = value ? options.find((o) => o.value === value) : undefined;
  const shown = selected ? `${title}: ${selected.label}` : allLabel;

  // Opening a menu you cannot then move through is worse than no menu: the
  // options carried role="option" and tabIndex={0} but no key handler, so a
  // keyboard user could reach them and had no way to choose one. Focus lands
  // on the first option when the list opens; ↑/↓ walk it, Enter chooses,
  // Escape closes and hands focus back to the chip that opened it.
  useEffect(() => {
    if (open) (listRef.current?.querySelector('[role="option"]') as HTMLElement | null)?.focus();
  }, [open]);

  const close = () => { setOpen(false); btnRef.current?.focus(); };
  const choose = (v: string | undefined) => { onSelect(v); close(); };
  const onOptionKey = (e: React.KeyboardEvent<HTMLLIElement>, v: string | undefined) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choose(v); return; }
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const sib = (e.key === "ArrowDown" ? e.currentTarget.nextElementSibling
                                         : e.currentTarget.previousElementSibling) as HTMLElement | null;
      sib?.focus();
    }
  };

  return (
    <div className="relative">
      <button ref={btnRef} type="button" className={chip} style={chipStyle(Boolean(value))}
              aria-haspopup="listbox" aria-expanded={open}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); }
                else if (e.key === "Escape" && open) { e.preventDefault(); setOpen(false); }
              }}
              onClick={() => setOpen((o) => !o)}>
        {shown}
      </button>
      {open && (
        <>
          {/* Full-screen click-catcher so choosing nothing still closes the
              list — a menu that only closes via selection traps the reader. */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <ul ref={listRef} role="listbox" aria-label={title}
              className="absolute z-20 mt-1 py-1 font-mono"
              style={{ minWidth: "100%", whiteSpace: "nowrap", background: "var(--paper)",
                       border: "1px solid var(--hairline)", boxShadow: "0 2px 8px rgba(10,15,24,0.15)" }}>
            <li role="option" aria-selected={!value} tabIndex={0}
                className="px-2.5 py-1 cursor-pointer hover:bg-[rgba(31,78,121,0.06)]"
                style={{ fontSize: 11, color: "var(--ink-3)" }}
                onKeyDown={(e) => onOptionKey(e, undefined)}
                onClick={() => choose(undefined)}>
              {allLabel}
            </li>
            {options.map((o) => (
              <li key={o.value} role="option" aria-selected={value === o.value} tabIndex={0}
                  className="px-2.5 py-1 cursor-pointer hover:bg-[rgba(31,78,121,0.06)]"
                  style={{ fontSize: 11, color: value === o.value ? "var(--accent-deep)" : "var(--ink-1)" }}
                  onKeyDown={(e) => onOptionKey(e, o.value)}
                  onClick={() => choose(o.value)}>
                {o.label}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export function DocumentFilters({ value, facets, onChange, isStaff }: {
  value: FilterValue; facets: DocumentFacets; onChange: (v: FilterValue) => void; isStaff: boolean;
}): JSX.Element {
  const [more, setMore] = useState(false);

  const kindOptions = Object.entries(facets.kinds).map(([k, n]) => ({
    value: k, label: `${KIND_LABEL[k] ?? k} (${n})`,
  }));
  const yearOptions = [
    ...facets.years.map((y) => ({ value: String(y), label: String(y) })),
    ...(facets.undated > 0 ? [{ value: "none", label: `ไม่ระบุวันที่ (${facets.undated})` }] : []),
  ];
  const directionOptions = Object.entries(DIRECTION_LABEL).map(([v, label]) => ({ value: v, label }));

  return (
    <div className="flex items-center gap-2 flex-wrap px-4 py-2 border-b"
         style={{ borderColor: "var(--hairline)" }}>
      <FacetDropdown title="ชนิด" allLabel="ชนิดทั้งหมด" value={value.kind}
                      options={kindOptions}
                      onSelect={(kind) => onChange({ ...value, kind })} />

      <FacetDropdown title="ปี" allLabel="ปีทั้งหมด" value={value.year}
                      options={yearOptions}
                      onSelect={(year) => onChange({ ...value, year })} />

      {/* Staff only. GET /api/intake HARD-forces current_only=true for every
          non-staff caller (routers/intake.py's CUSTOMER-FACING RULE: a
          superseded revision must never reach a customer, ISO/IEC 17020 §8.4).
          Offered to a customer, this chip fired a fetch, relabelled itself
          "รวมฉบับที่ถูกแทนที่", and changed nothing — a control claiming to show
          superseded revisions that cannot. DocumentsScreen gates the equivalent
          toggle the same way. */}
      {isStaff && (
        <button className={chip} style={chipStyle(value.currentOnly)}
                onClick={() => onChange({ ...value, currentOnly: !value.currentOnly })}>
          {value.currentOnly ? "ฉบับปัจจุบัน" : "รวมฉบับที่ถูกแทนที่"}
        </button>
      )}

      <button className={chip} style={chipStyle(false)} onClick={() => setMore((v) => !v)}>
        ตัวกรองเพิ่มเติม
      </button>

      {more && (
        <FacetDropdown title="ทิศทาง" allLabel="ทิศทางทั้งหมด" value={value.direction}
                        options={directionOptions}
                        onSelect={(direction) => onChange({ ...value, direction })} />
      )}
    </div>
  );
}
